import React, { useEffect, useMemo, useState } from 'react'
import {
  Skeleton,
  Table as TableCortex,
  Td,
  THead,
  TRow,
  useDimensions
} from '@tecsinapse/cortex-react'
import { Trans } from '@lingui/react/macro'
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table'
import { rowsPerPageOptions } from '@/core/utils/rows-per-page'
import { Table } from '@/core/components/index'
import { ColumnsProps } from '../types/types'
import { GetPastaQuery } from '@/core/types/graphql'
import { useParams } from 'react-router'
import { useTabelaBuscaArquivo } from '../hooks/use-tabela-busca-arquivo'
import { parseAsIndex, parseAsNumberLiteral, useQueryStates } from 'nuqs'
import { initialFilters } from '../utils/constants'
import { columnsPastas } from './columns-pastas'
import { ItemBibliotecaHomeCardType } from './item-biblioteca-home-card'

interface TabelaPastasPropsProps extends ColumnsProps {
  searchText: string
  pastaAtual: GetPastaQuery['pasta']
}

export const TabelaBuscaArquivo = ({
  onClickItem,
  selectedRows,
  isItemSelected,
  onDoubleClick,
  searchText
}: TabelaPastasPropsProps) => {
  const [pagination, setPagination] = useQueryStates(
    {
      pageIndex: parseAsIndex.withDefault(0),
      pageSize: parseAsNumberLiteral(rowsPerPageOptions).withDefault(
        rowsPerPageOptions[0]
      )
    },
    { urlKeys: { pageIndex: 'page' } }
  )
  const [filteredItems, setFilteredItems] =
    useState<ItemBibliotecaHomeCardType[]>(initialFilters)
  const params = useParams()

  const { buscaBibliotecaPaged, loading } = useTabelaBuscaArquivo({
    pagination,
    searchText
  })

  const fetchedItems = useMemo(
    () => buscaBibliotecaPaged?.content ?? [],
    [buscaBibliotecaPaged]
  )

  useEffect(() => {
    setFilteredItems(() => {
      if (params.idPasta) {
        return fetchedItems.filter(
          item => item?.pastaPai?.id === params.idPasta
        )
      }
      return fetchedItems
    })
  }, [fetchedItems])

  const totalElements = useMemo(
    () => filteredItems?.length ?? 0,
    [filteredItems]
  )

  const { width } = useDimensions()
  const isMobile = width <= 768

  const table = useReactTable({
    data: filteredItems,
    columns: columnsPastas({
      isItemSelected,
      selectedRows,
      onClickItem,
      isMobile
    }),
    getCoreRowModel: getCoreRowModel(),
    rowCount: totalElements,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination
    }
  })

  return (
    <>
      {loading ? (
        <Skeleton className='h-28 w-full rounded-mili' />
      ) : (
        <TableCortex className='w-full mb-deca'>
          <THead>
            <TRow>
              <Td colSpan={99} className={'py-centi'}>
                <div
                  className={
                    'flex w-full flex-row items-center justify-between'
                  }
                >
                  <p className='text-h3 font-bold'>
                    <Trans>Pastas e Arquivos</Trans>
                  </p>
                </div>
              </Td>
            </TRow>
          </THead>
          <Table.Body
            table={table}
            onClickRow={onClickItem}
            onDoubleClickRow={onDoubleClick}
            loading={loading}
          />
          <Table.Footer
            table={table}
            rowsPerPageOptions={rowsPerPageOptions}
            loading={loading}
            countList={totalElements}
          />
        </TableCortex>
      )}
    </>
  )
}
